<script>
    import { Input } from '@/components/ui/input/index.js';
    import InputError from '@/components/InputError.svelte';
    import { Label } from '@/components/ui/label/index.js';
    import FieldDescription from '@/components/Form/Inputs/FieldDescription.svelte';
    import { createFieldValidation, isFieldRequired } from '@/lib/fieldValidation.js';

    let { value = $bindable(), error = null, field = $bindable() , onkeydown, showValidation = false} = $props();
    let attributes = $derived(field?.attributes ? JSON.parse(field.attributes) : {});
    let isDirty = $state(false);

    const isRequired = $derived(isFieldRequired(field, attributes?.required));
    const isEmptyRequired = $derived(isRequired && (!value || value?.toString().trim() === ''));

    // Get min/max from attributes for number fields
    const minValue = $derived(field?.type === 'number' ? attributes?.min : undefined);
    const maxValue = $derived(field?.type === 'number' ? attributes?.max : undefined);

    // Get validation state
    const validation = $derived.by(() => {
        return createFieldValidation({
            field,
            value,
            isDirty,
            required: isRequired,
            min: minValue,
            max: maxValue
        });
    });

    // Compute CSS classes - combine showValidation mode with standard validation
    const validationClasses = $derived(() => {
        // If there's a server error, always show error state
        if (error) {
            return 'input-error';
        }
        // In showValidation mode, show success if valid and no errors
        if (showValidation && validation.isValid) {
            return 'input-success';
        }
        // Otherwise use standard validation classes
        return validation.classes;
    });

    // Mark field as dirty on blur or input
    function handleBlur() {
        isDirty = true;
    }

    function handleInput() {
        if (value && value.toString().trim().length > 0) {
            isDirty = true;
        }
    }
</script>

<div class="flex-auto">
    <div class="grid gap-2">
        <Label for={field.name}
            >{field.label}{#if isRequired}<span class="text-destructive">*</span
                >{/if}</Label
        >
        {#if field.description}
            <FieldDescription>{field.description}</FieldDescription>
        {/if}

        <div class="flex gap-1">
            {#if field.units?.prepend}
                <p class="h-full place-content-center">{field.units.prepend}</p>
            {/if}

            <Input
                id={field.name}
                name={field.column_name}
                class="block w-full {validationClasses()}"
                bind:value
                placeholder={field.placeholder ?? field.label}
                type={field.type}
                aria-invalid={error || (isEmptyRequired && isDirty) ? true : undefined}
                onblur={handleBlur}
                oninput={handleInput}
                {onkeydown}
                {...attributes}
            />

            {#if field.units?.append}
                <p class="h-full place-content-center">{field.units.append}</p>
            {/if}
        </div>

        {#if validation.errorMessage}
            <InputError class="mt-2" message={validation.errorMessage} />
        {:else}
            <InputError class="mt-2" message={error} />
        {/if}
    </div>
</div>
